import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageSpinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { ArrowLeft, Send, MessageSquare, Plus, Users, Briefcase, AlertTriangle, Loader2 } from "lucide-react";

interface Channel {
    id: string;
    channel_type: string;
    name: string | null;
    job_id: string | null;
    unread_count: number;
    last_message: string | null;
    last_message_at: string | null;
}

interface Message {
    id: string;
    sender_id: string;
    body: string;
    sender_name: string;
    sender_role: string;
    ai_flagged: boolean;
    created_at: string;
}

interface ChatUser {
    id: string;
    first_name: string;
    last_name: string;
    role_name: string;
    display_name: string | null;
}

function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

// ─── Channel List ─────────────────────────────────────────────────────

function ChannelList({ onSelect, onNewChat }: { onSelect: (id: string) => void; onNewChat: () => void }) {
    const channels = useQuery({
        queryKey: ["chat", "channels"],
        queryFn: () => api.get<{ channels: Channel[] }>("/api/chat/channels"),
        refetchInterval: 15000,
    });

    return (
        <Card className="overflow-hidden">
            <CardContent className="p-4 space-y-3 pt-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold">Messages</h2>
                    <Button size="sm" variant="outline" onClick={onNewChat}>
                        <Plus className="h-4 w-4 mr-1" />
                        New Chat
                    </Button>
                </div>
                {channels.isLoading && <PageSpinner />}
                {channels.data?.channels.length === 0 && (
                    <div className="text-center py-8">
                        <MessageSquare className="h-10 w-10 mx-auto text-foreground/60 mb-2" />
                        <p className="text-sm text-foreground/80">No conversations yet.</p>
                        <Button size="sm" className="mt-3" onClick={onNewChat}>
                            Start a conversation
                        </Button>
                    </div>
                )}
                <div className="space-y-2">
                    {channels.data?.channels.map((ch) => (
                        <div
                            key={ch.id}
                            className="cursor-pointer rounded-lg border border-border/60 bg-background/40 hover:bg-secondary/30 transition-colors p-3 flex items-center gap-3"
                            onClick={() => onSelect(ch.id)}
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
                                {ch.channel_type === "job" ? (
                                    <Briefcase className="h-4 w-4" />
                                ) : (
                                    <Users className="h-4 w-4" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm truncate">{ch.name || "Direct message"}</span>
                                    {ch.unread_count > 0 && (
                                        <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">
                                            {ch.unread_count}
                                        </Badge>
                                    )}
                                </div>
                                {ch.last_message && (
                                    <p className="text-xs text-foreground/80 truncate mt-0.5">{ch.last_message}</p>
                                )}
                            </div>
                            {ch.last_message_at && (
                                <span className="text-[10px] text-foreground/80 shrink-0">
                                    {relativeTime(ch.last_message_at)}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

// ─── Thread View ──────────────────────────────────────────────────────

function ThreadView({ channelId, onBack }: { channelId: string; onBack: () => void }) {
    const { user } = useAuth();
    const qc = useQueryClient();
    const [text, setText] = useState("");
    const bottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const messages = useQuery({
        queryKey: ["chat", "messages", channelId],
        queryFn: () => api.get<{ messages: Message[] }>(`/api/chat/channels/${channelId}/messages`),
        refetchInterval: 5000,
    });

    // Same cache the list view fills — a name for the header, no extra fetch.
    const channelsQ = useQuery({
        queryKey: ["chat", "channels"],
        queryFn: () => api.get<{ channels: Channel[] }>("/api/chat/channels"),
        staleTime: 60_000,
    });
    const channelName = channelsQ.data?.channels.find((c) => c.id === channelId)?.name ?? null;

    // SSE for real-time messages
    useEffect(() => {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        let es: EventSource | null = null;
        try {
            es = new EventSource(`/api/sse?token=${encodeURIComponent(token)}`);
            es.addEventListener("chat:new_message", (e) => {
                const data = JSON.parse(e.data);
                if (data.channel_id === channelId) {
                    qc.invalidateQueries({ queryKey: ["chat", "messages", channelId] });
                    qc.invalidateQueries({ queryKey: ["chat", "channels"] });
                    qc.invalidateQueries({ queryKey: ["chat", "unread-total"] });
                }
            });
        } catch {
            /* SSE not available */
        }
        return () => es?.close();
    }, [channelId, qc]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.data?.messages.length]);

    const sendMessage = useMutation({
        mutationFn: async (body: string) => {
            return api.post(`/api/chat/channels/${channelId}/messages`, { body });
        },
        onSuccess: () => {
            setText("");
            qc.invalidateQueries({ queryKey: ["chat", "messages", channelId] });
            qc.invalidateQueries({ queryKey: ["chat", "channels"] });
            inputRef.current?.focus();
        },
    });

    const handleSend = useCallback(() => {
        if (text.trim()) sendMessage.mutate(text.trim());
    }, [text, sendMessage]);

    return (
        <Card className="flex flex-col h-[calc(100vh-5rem)] overflow-hidden">
            <div className="flex items-center gap-2 py-2 border-b border-border/70 px-3">
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Back" title="Back" onClick={onBack}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <span className="font-semibold text-sm truncate">{channelName ?? "Chat"}</span>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                {messages.isLoading && <PageSpinner />}
                {messages.data?.messages.map((msg) => {
                    const isMe = msg.sender_id === user?.id;
                    return (
                        <div
                            key={msg.id}
                            className={cn("flex flex-col max-w-[85%]", isMe ? "ml-auto items-end" : "items-start")}
                        >
                            {!isMe && (
                                <span className="text-[10px] text-foreground/80 mb-0.5 px-1">
                                    {msg.sender_name}
                                    {msg.sender_role && ` · ${msg.sender_role}`}
                                </span>
                            )}
                            <div
                                className={cn(
                                    "rounded-2xl px-3 py-2 text-sm break-words",
                                    isMe
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-secondary text-secondary-foreground",
                                    msg.ai_flagged && "ring-2 ring-red-400",
                                )}
                            >
                                {msg.body}
                                {msg.ai_flagged && (
                                    <span title="Flagged for office review" aria-label="Flagged for office review">
                                        <AlertTriangle className="h-3 w-3 inline-block ml-1 text-red-400" />
                                    </span>
                                )}
                            </div>
                            <span className="text-[10px] text-foreground/80 mt-0.5 px-1">
                                {relativeTime(msg.created_at)}
                            </span>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            <div className="border-t border-border/70 p-3 flex gap-2">
                <Input
                    ref={inputRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    placeholder="Type a message..."
                    className="flex-1"
                    autoFocus
                />
                <Button
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    aria-label="Send message"
                    title="Send message"
                    onClick={handleSend}
                    disabled={sendMessage.isPending || !text.trim()}
                >
                    {sendMessage.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4" />
                    )}
                </Button>
            </div>
        </Card>
    );
}

// ─── New Chat Picker ──────────────────────────────────────────────────

function NewChatPicker({ onCreated, onBack }: { onCreated: (channelId: string) => void; onBack: () => void }) {
    const users = useQuery({
        queryKey: ["chat", "users"],
        queryFn: () => api.get<{ users: ChatUser[] }>("/api/chat/users"),
    });

    const createChannel = useMutation({
        mutationFn: async (userId: string) => {
            const resp = await api.post<{ channel: { id: string } }>("/api/chat/channels", {
                channelType: "direct",
                memberIds: [userId],
            });
            return resp.channel.id;
        },
        onSuccess: (channelId) => onCreated(channelId),
    });

    return (
        <Card className="overflow-hidden">
            <CardContent className="p-4 space-y-3 pt-4">
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        aria-label="Back"
                        title="Back"
                        onClick={onBack}
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h2 className="text-lg font-bold">New Conversation</h2>
                </div>
                {users.isLoading && <PageSpinner />}
                <div className="space-y-2">
                    {users.data?.users.map((u) => (
                        <div
                            key={u.id}
                            className="cursor-pointer rounded-lg border border-border/60 bg-background/40 hover:bg-secondary/30 transition-colors p-3 flex items-center gap-3"
                            onClick={() => createChannel.mutate(u.id)}
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary dark:bg-primary/15 dark:text-primary text-sm font-semibold shrink-0">
                                {(u.display_name ?? u.first_name)?.charAt(0)?.toUpperCase() ?? "U"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">
                                    {u.display_name || `${u.first_name} ${u.last_name}`}
                                </div>
                                <div className="text-xs text-foreground/80 capitalize">{u.role_name}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

// ─── Main Chat Page ───────────────────────────────────────────────────

export default function FieldChatPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    useQueryClient();
    const jobId = searchParams.get("jobId");

    const [view, setView] = useState<"list" | "thread" | "new">("list");
    const [activeChannel, setActiveChannel] = useState<string | null>(null);

    // If opened with ?jobId=, auto-get/create the job channel
    const jobChannel = useQuery({
        queryKey: ["chat", "job-channel", jobId],
        queryFn: () => api.get<{ channelId: string }>(`/api/chat/job/${jobId}/channel`),
        enabled: !!jobId,
    });

    useEffect(() => {
        if (jobChannel.data?.channelId) {
            setActiveChannel(jobChannel.data.channelId);
            setView("thread");
        }
    }, [jobChannel.data?.channelId]);

    if (view === "thread" && activeChannel) {
        return (
            <div className="max-w-2xl mx-auto">
                <ThreadView
                    channelId={activeChannel}
                    onBack={() => {
                        if (jobId) {
                            navigate(`/field/jobs/${jobId}`);
                        } else {
                            setView("list");
                            setActiveChannel(null);
                        }
                    }}
                />
            </div>
        );
    }

    if (view === "new") {
        return (
            <div className="max-w-2xl mx-auto px-4 py-3">
                <NewChatPicker
                    onCreated={(id) => {
                        setActiveChannel(id);
                        setView("thread");
                    }}
                    onBack={() => setView("list")}
                />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2 mb-3">
                <button
                    onClick={() => navigate("/field")}
                    className="flex items-center gap-1.5 text-sm text-shell-foreground/80 hover:text-shell-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </button>
            </div>
            <ChannelList
                onSelect={(id) => {
                    setActiveChannel(id);
                    setView("thread");
                }}
                onNewChat={() => setView("new")}
            />
        </div>
    );
}
